import React, { useState } from 'react';
import { ArrowRight, Compass } from 'lucide-react';
import './InlineSessionSuggestion.css';

/**
 * InlineSessionSuggestion - Clickable suggestion to spawn an exploration session
 *
 * Rendered below AI messages when the AI suggests exploring a topic
 * in a dedicated session. Creates a child session linked to the parent.
 *
 * Props:
 * - suggestion: Object with { type, template, label, context }
 * - parentSessionId: UUID of the parent (logline workshop) session
 * - storyId: UUID of the story
 * - onSessionCreated: Callback with new session ID for navigation
 */
export function InlineSessionSuggestion({
  suggestion,
  parentSessionId,
  storyId,
  onSessionCreated
}) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = async () => {
    setCreating(true);
    setError(null);

    try {
      // Create new exploration session with parent reference
      const response = await fetch(`/api/stories/${storyId}/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template: suggestion.template,
          guidance_mode: 'focus',
          parent_session_id: parentSessionId,
          name: `${suggestion.label}: ${suggestion.context || 'Exploration'}`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create exploration session');
      }

      const data = await response.json();

      // Navigate to new session
      if (onSessionCreated && data.data?.id) {
        onSessionCreated(data.data.id);
      }
    } catch (err) {
      console.error('Failed to create exploration session:', err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="inline-session-suggestion">
      <button
        onClick={handleClick}
        disabled={creating}
        className="inline-session-suggestion__button"
      >
        <Compass className="inline-session-suggestion__icon" />
        <div className="inline-session-suggestion__content">
          <span className="inline-session-suggestion__label">{suggestion.label}</span>
          {suggestion.context && (
            <span className="inline-session-suggestion__context">{suggestion.context}</span>
          )}
        </div>
        <ArrowRight className="inline-session-suggestion__arrow" />
      </button>
      {error && (
        <div className="inline-session-suggestion__error">{error}</div>
      )}
    </div>
  );
}

export default InlineSessionSuggestion;
